import { useMemo, useState } from "react";

import { cn } from "../../lib/utils";

/** Selo de posição do ranking: medalha (ouro/prata/bronze) no top 3, número
 * simples depois. Fonte única usada por todos os rankings (turmas, regiões,
 * escolas). */
const MEDAL = {
  1: "bg-[#fef3c7] text-[#b45309]",
  2: "bg-[#e5e7eb] text-[#4b5563]",
  3: "bg-[#fde4cf] text-[#b45309]"
};
/* Posições 1–3 ganham medalha; da 4ª em diante, só o número.

   Os dois casos ocupam a MESMA largura (w-6) e são centralizados: sem isso o
   número solto encostava no nome e a coluna desalinhava conforme o ranking
   passava de um para dois dígitos. */
export function RankBadge({ pos }) {
  if (pos <= 3) {
    return (
      <span
        className={`inline-grid h-6 w-6 flex-none place-items-center rounded-lg text-[12px] font-bold ${MEDAL[pos]}`}
      >
        {pos}
      </span>
    );
  }
  return (
    <span className="tnum inline-grid h-6 w-6 flex-none place-items-center text-[12px] text-[var(--color-ink-faint)]">
      {pos}
    </span>
  );
}

/** Tabela genérica ordenável, com fallback em cards empilhados no mobile
 * (mesmo padrão responsivo já validado em AlunosTabela). */
export function RankingTabela({
  linhas,
  colunas,
  rowKey,
  sortDefault,
  onRowClick,
  rank
}) {
  const [sortKey, setSortKey] = useState(sortDefault ?? colunas[0]?.key);
  const [dir, setDir] = useState(-1);

  const ordenadas = useMemo(() => {
    const col = colunas.find(c => c.key === sortKey);
    if (!col?.valor) return linhas;
    const arr = [...linhas];
    arr.sort((a, b) => (col.valor(a) - col.valor(b)) * dir);
    return arr;
  }, [linhas, colunas, sortKey, dir]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">
        <thead className="hidden sm:table-header-group">
          <tr className="sm:border-b-2 sm:border-[var(--color-border-strong)]">
            {colunas.map(c => {
              const ativo = sortKey === c.key;
              /* Mesmo tratamento do cabeçalho da tabela de alunos: <button> com
                 `pointerEvents` inline. O AVA declara `.hidden { pointer-events:
                 none }` e o cabeçalho usa `hidden sm:table-header-group`; o
                 Tailwind sobrescreve só o `display`, então o clique morria.
                 Colunas sem `valor` não são ordenáveis e não viram botão. */
              return (
                <th
                  key={c.key}
                  // eslint-disable-next-line no-nested-ternary
                  aria-sort={
                    // eslint-disable-next-line no-nested-ternary
                    !c.valor || !ativo
                      ? "none"
                      : dir === 1
                      ? "ascending"
                      : "descending"
                  }
                  className={cn(
                    "whitespace-nowrap px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-faint)]",
                    c.num ? "text-right" : "text-left"
                  )}
                  style={{ pointerEvents: "auto" }}
                >
                  {c.valor ? (
                    <button
                      type="button"
                      style={{ pointerEvents: "auto", cursor: "pointer" }}
                      className={cn(
                        "inline-flex select-none items-center gap-1 border-0 bg-transparent p-0 text-[10.5px] font-semibold uppercase tracking-[0.06em] transition-colors",
                        ativo
                          ? "text-[var(--color-ink)]"
                          : "text-[var(--color-ink-faint)] hover:text-[var(--color-ink-soft)]"
                      )}
                      onClick={() => {
                        if (ativo) setDir(d => (d === 1 ? -1 : 1));
                        else {
                          setSortKey(c.key);
                          setDir(-1);
                        }
                      }}
                    >
                      {c.label}
                      <span className={ativo ? "opacity-70" : "opacity-30"}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {!ativo ? "▼" : dir === 1 ? "▲" : "▼"}
                      </span>
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordenadas.map((row, idx) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                // ≥sm: listagem no padrão do AVA — linha separada por borda de
                // 1px, zebra nas ímpares e hover perceptível (o Bootstrap usa
                // rgba(0,0,0,.05) e .075; aqui o equivalente em tokens).
                "transition-colors sm:table-row sm:border-b sm:border-[var(--color-border)]",
                "sm:odd:bg-[var(--color-surface-2)]/60",
                onRowClick &&
                  "cursor-pointer sm:hover:bg-[var(--color-brand-dim)]",
                // <sm: card empilhado. Uma borda só — a do card. O cabeçalho
                // (posição + nome) separa-se das métricas por espaço, não por
                // outra linha.
                "mb-3 grid grid-cols-2 gap-x-4 gap-y-3.5 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:mb-0 sm:rounded-none sm:border-x-0 sm:border-t-0 sm:bg-transparent sm:p-0 sm:shadow-none"
              )}
            >
              {colunas.map((c, i) => (
                <td
                  key={c.key}
                  className={cn(
                    i === 0 && "col-span-2",
                    "sm:px-3 sm:py-2.5",
                    c.num && "sm:text-right"
                  )}
                >
                  {i > 0 && (
                    <span className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-faint)] sm:hidden">
                      {c.label}
                    </span>
                  )}
                  {rank && i === 0 ? (
                    <span className="flex items-center gap-3 text-[13.5px] font-medium sm:text-[12.5px] sm:font-normal">
                      <RankBadge pos={idx + 1} />
                      {c.render(row)}
                    </span>
                  ) : (
                    c.render(row)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
